/**
 * VoiceNoteDialog — reusable modal that records mic audio, uploads to
 * `/api/voice-notes`, and surfaces the transcript + AI summary.
 *
 * Used in three places:
 *   - Top bar (`VoiceNoteButton`) — global, no patient pre-attached.
 *   - Patient profile — `patientId`/`patientName` prefilled.
 *   - Form fill page — `formSubmissionId` carried through.
 *
 * Browser MediaRecorder API → produces a `webm/opus` blob → multipart
 * upload. The dialog stays open during processing and shows the summary
 * inline; user can copy it or trigger a `.txt` download.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Mic, Square, Loader2, Download, Copy, Trash2, FileText, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function authHeader() {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function fmtTime(s) {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

export default function VoiceNoteDialog({ open, onOpenChange, patientId = null, patientName = null, formSubmissionId = null, onSaved }) {
  const [phase, setPhase] = useState('idle'); // idle | recording | processing | done | error
  const [elapsed, setElapsed] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const [title, setTitle] = useState('');
  const [result, setResult] = useState(null); // saved voice note doc
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const tickerRef = useRef(null);
  const startedAtRef = useRef(null);

  useEffect(() => {
    // Reset on dialog open
    if (open) {
      setPhase('idle');
      setElapsed(0);
      setErrorMsg('');
      setTitle('');
      setResult(null);
      chunksRef.current = [];
    } else {
      // Clean up when closed mid-recording
      cleanupRecorder();
    }
    return () => { cleanupRecorder(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const cleanupRecorder = () => {
    if (tickerRef.current) { clearInterval(tickerRef.current); tickerRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
  };

  const startRecording = async () => {
    setErrorMsg('');
    setPhase('idle');
    chunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
      const rec = new MediaRecorder(stream, { mimeType: mime });
      mediaRecorderRef.current = rec;
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        // Hand-off to upload
        uploadBlob(new Blob(chunksRef.current, { type: mime }));
      };
      rec.start();
      startedAtRef.current = Date.now();
      tickerRef.current = setInterval(() => {
        setElapsed((Date.now() - startedAtRef.current) / 1000);
      }, 250);
      setPhase('recording');
    } catch (err) {
      setErrorMsg(err?.message || 'Microphone access denied. Please allow mic permissions.');
      setPhase('error');
    }
  };

  const stopRecording = () => {
    if (!mediaRecorderRef.current) return;
    setPhase('processing');
    try { mediaRecorderRef.current.stop(); } catch { /* ignore */ }
    if (tickerRef.current) { clearInterval(tickerRef.current); tickerRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  const uploadBlob = async (blob) => {
    if (!blob || blob.size === 0) {
      setErrorMsg('No audio captured');
      setPhase('error');
      return;
    }
    setPhase('processing');
    try {
      const fd = new FormData();
      const ext = blob.type.includes('webm') ? 'webm' : 'wav';
      fd.append('audio', blob, `voice_note.${ext}`);
      if (title.trim()) fd.append('title', title.trim());
      if (patientId) fd.append('patient_id', patientId);
      if (formSubmissionId) fd.append('form_submission_id', formSubmissionId);
      fd.append('duration_seconds', String(elapsed));
      const r = await axios.post(`${API_URL}/api/voice-notes`, fd, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      });
      setResult(r.data);
      setPhase('done');
      toast.success('Voice note saved');
      if (onSaved) onSaved(r.data);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Upload failed';
      setErrorMsg(typeof msg === 'string' ? msg : JSON.stringify(msg));
      setPhase('error');
      toast.error('Failed to process voice note');
    }
  };

  const handleDownload = async () => {
    if (!result?.id) return;
    try {
      const r = await axios.get(`${API_URL}/api/voice-notes/${result.id}/download`, {
        headers: authHeader(),
        responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(result.title || 'voice_note').replace(/\s+/g, '_')}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const handleCopy = async () => {
    if (!result) return;
    const t = [
      result.summary,
      '',
      'Key points:',
      ...(result.bullets || []).map(b => `• ${b}`),
      '',
      'Action items:',
      ...(result.action_items || []).map((a, i) => `${i + 1}. ${a}`),
    ].join('\n');
    try {
      await navigator.clipboard.writeText(t);
      toast.success('Summary copied');
    } catch { toast.error('Clipboard blocked'); }
  };

  const handleDiscard = async () => {
    if (!result?.id) { onOpenChange(false); return; }
    try {
      await axios.delete(`${API_URL}/api/voice-notes/${result.id}`, { headers: authHeader() });
      toast.success('Note discarded');
    } catch { /* ignore */ }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (phase === 'recording' || phase === 'processing') return; onOpenChange(v); }}>
      <DialogContent className="max-w-2xl" data-testid="voice-note-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0A2342]">
            <Mic className="w-5 h-5 text-[#0E7490]" />
            Voice Note
            {patientName && <span className="ml-2 text-xs font-normal text-[#64748B]">· Patient: {patientName}</span>}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4" data-testid="voice-note-body">
          {phase !== 'done' && (
            <div>
              <Label className="text-xs text-[#64748B]">Title (optional)</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Mrs. Park morning round"
                className="h-9 text-sm"
                disabled={phase === 'recording' || phase === 'processing'}
                data-testid="voice-note-title-input"
              />
            </div>
          )}

          {phase === 'idle' && (
            <div className="text-center py-6">
              <div className="text-xs text-[#64748B] mb-3">Tap to begin recording. Whisper transcribes, GPT-5.2 summarizes — usually under 30s.</div>
              <Button onClick={startRecording} className="careiq-btn-primary" data-testid="voice-note-start-btn">
                <Mic className="w-4 h-4 mr-2" /> Start recording
              </Button>
            </div>
          )}

          {phase === 'recording' && (
            <div className="text-center py-6 space-y-3" data-testid="voice-note-recording">
              <div className="inline-flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#991B1B] animate-pulse" />
                <span className="text-2xl font-mono text-[#0A2342]">{fmtTime(elapsed)}</span>
              </div>
              <div className="text-xs text-[#64748B]">Recording — speak naturally.</div>
              <Button onClick={stopRecording} variant="destructive" data-testid="voice-note-stop-btn">
                <Square className="w-4 h-4 mr-2" /> Stop & process
              </Button>
            </div>
          )}

          {phase === 'processing' && (
            <div className="text-center py-8 space-y-2" data-testid="voice-note-processing">
              <Loader2 className="w-8 h-8 text-[#0E7490] animate-spin mx-auto" />
              <div className="text-sm text-[#0A2342] font-medium">Transcribing & summarizing…</div>
              <div className="text-xs text-[#64748B]">This typically takes 10-30 seconds.</div>
            </div>
          )}

          {phase === 'error' && (
            <div className="bg-[#FEE2E2] border border-[#FCA5A5] rounded p-3 text-sm text-[#991B1B] flex gap-2" data-testid="voice-note-error">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <div className="font-medium">Could not process recording</div>
                <div className="text-xs">{errorMsg}</div>
                <Button onClick={() => setPhase('idle')} variant="outline" size="sm" className="mt-2 h-7 text-xs">Try again</Button>
              </div>
            </div>
          )}

          {phase === 'done' && result && (
            <div className="space-y-3" data-testid="voice-note-result">
              <div>
                <div className="text-xs font-semibold text-[#0A2342] mb-1">Summary</div>
                <p className="text-sm text-[#0A2342] bg-[#F0FDFA] border border-[#A7F3D0] rounded p-3 whitespace-pre-wrap" data-testid="voice-note-summary">{result.summary || '(empty)'}</p>
              </div>

              {result.bullets?.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-[#0A2342] mb-1">Key points</div>
                  <ul className="text-sm text-[#0A2342] list-disc list-inside space-y-0.5" data-testid="voice-note-bullets">
                    {result.bullets.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                </div>
              )}

              {result.action_items?.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-[#0A2342] mb-1">Action items</div>
                  <ol className="text-sm text-[#0A2342] list-decimal list-inside space-y-0.5" data-testid="voice-note-actions">
                    {result.action_items.map((a, i) => <li key={i}>{a}</li>)}
                  </ol>
                </div>
              )}

              <details className="text-xs">
                <summary className="cursor-pointer text-[#0E7490] font-medium">View full transcript</summary>
                <Textarea
                  value={result.transcript || ''}
                  readOnly
                  className="mt-2 text-xs h-32"
                  data-testid="voice-note-transcript"
                />
              </details>

              <div className="flex flex-wrap gap-2 pt-2 border-t border-[#E2E8F0]">
                <Button onClick={handleDownload} variant="outline" size="sm" data-testid="voice-note-download-btn">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Download .txt
                </Button>
                <Button onClick={handleCopy} variant="outline" size="sm" data-testid="voice-note-copy-btn">
                  <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy summary
                </Button>
                <Button onClick={handleDiscard} variant="outline" size="sm" className="text-[#991B1B] hover:bg-[#991B1B]/5" data-testid="voice-note-discard-btn">
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Discard
                </Button>
                <div className="ml-auto">
                  <a href="/voice-notes" className="text-xs text-[#0E7490] hover:underline inline-flex items-center gap-1">
                    <FileText className="w-3 h-3" /> All my notes
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
